import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageCircle, Calendar } from 'lucide-react';
import { trackWhatsAppClick } from '../utils/analytics';

/**
 * Pair of persistent floating CTAs shown on every page.
 * - Large WhatsApp button with visible label (mobile shrinks to icon + short text)
 * - "BOOK NOW" button that always scrolls to (or navigates to) the booking form
 *
 * Both buttons stack vertically bottom-right and stay above WhatsApp on mobile.
 */
export const FloatingCTAs = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  const lang = (i18n.language || 'en').split('-')[0];

  // Reveal after tiny scroll so it doesn't block the hero's first paint
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 80);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleWhatsApp = () => {
    trackWhatsAppClick('floating_button');
  };

  const handleBookNow = (e) => {
    e.preventDefault();
    const target = document.getElementById('booking');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      // We are on a page without the booking section — go home and scroll
      navigate(`/${lang}#booking`);
      setTimeout(() => {
        const el = document.getElementById('booking');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 400);
    }
  };

  const whatsappMsg = encodeURIComponent(t('whatsapp.prefill_message'));
  const whatsappHref = `https://wa.me/351932651940?text=${whatsappMsg}`;

  return (
    <div
      className={`fixed right-3 md:right-5 bottom-16 md:bottom-20 z-[60] flex flex-col gap-2.5 md:gap-3 items-end transition-all duration-500 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
      data-testid="floating-ctas"
      aria-hidden={!visible}
    >
      {/* BOOK NOW — gold pill */}
      <button
        onClick={handleBookNow}
        className="group flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 px-4 py-3 md:px-6 md:py-3.5 text-white shadow-2xl ring-2 ring-amber-300/40 hover:from-amber-400 hover:to-amber-500 hover:scale-[1.03] transition-all duration-300"
        aria-label={t('floating.book_now_aria')}
        data-testid="floating-book-now"
      >
        <Calendar className="h-5 w-5 md:h-5 md:w-5 flex-shrink-0" />
        <span className="text-sm md:text-base font-bold uppercase tracking-wider whitespace-nowrap">
          {t('floating.book_now')}
        </span>
      </button>

      {/* WHATSAPP — green pill, wider than usual, always shows label */}
      <a
        href={whatsappHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleWhatsApp}
        className="group relative flex items-center gap-2.5 rounded-full bg-green-500 px-4 py-3 md:px-6 md:py-3.5 text-white shadow-2xl ring-2 ring-green-300/40 hover:bg-green-600 hover:scale-[1.03] transition-all duration-300"
        aria-label={t('floating.whatsapp_aria')}
        data-testid="floating-whatsapp"
      >
        {/* Pulse */}
        <span
          className="pointer-events-none absolute -left-1 -top-1 -right-1 -bottom-1 rounded-full bg-green-400 opacity-40 animate-ping"
          aria-hidden
        />
        <MessageCircle className="relative h-5 w-5 md:h-6 md:w-6 flex-shrink-0" />
        <span className="relative text-sm md:text-base font-semibold whitespace-nowrap">
          {t('floating.whatsapp_label')}
        </span>
      </a>
    </div>
  );
};

export default FloatingCTAs;
